import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import currency from 'currency.js';

/**
 * One product on the Announcements & Gifts shelf.
 *
 * No card border, no shadow, no badges — on a white page the artwork does the
 * separating, and a white card on a white ground is just padding.
 *
 * The preview inside the square is passed in rather than built here: it is the
 * customer's own portrait composited into the product, which is the whole point
 * of this shelf ("gifts made from your own portraits"), and that compositing
 * lives in DisplayableSheet. This component owns the BOX — its shape, its
 * ground, its radius — and the type and the button beneath it.
 */

function CheckIcon() {
  return (
    <svg
      className="h-[18px] w-[18px]"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <path d="M3.5 8.5l3 3 6-6.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Art that cannot be drawn at all. Labelled rather than left blank: a silently
// empty square reads as a page that failed to load, and this one is reachable
// in production the moment an add-on is put on the shelf without an image.
function PhotoPlaceholder() {
  return (
    <div
      className={'flex h-full w-full items-center justify-center border-2 border-dashed '
        + 'border-gift-lineStrong'}
    >
      <span className="text-[13px] font-semibold uppercase tracking-wide text-gift-inkSubtle">
        Product photo
      </span>
    </div>
  );
}

export default function GiftTile({
  name, price, priceNode, preview, imageUrl, isAdded, onCustomize, onOpenAdded,
}) {
  return (
    /* ⚠️ `relative` and `group` ARE LOAD-BEARING. The name's button stretches an
       ::after over this element to make the WHOLE tile the target, and the hover
       lift is driven off `group` -- see the button below. */
    <div className="group relative flex h-full flex-col transition-transform duration-150 hover:-translate-y-[3px]">
      {/* SQUARE, always. The products are wildly different shapes — a mug, a
          wallet sheet, a landscape announcement — and letting each set its own
          height made the grid's rows ragged. */}
      <div className="relative aspect-square w-full overflow-hidden rounded-gift-img bg-gift-tile">
        {/* eslint-disable-next-line no-nested-ternary */}
        {preview || (imageUrl
          ? <img src={imageUrl} alt={`${name} preview`} className="h-full w-full object-cover" />
          : <PhotoPlaceholder />)}

        {/* ⚠️ THE ADDED STATE MOVED HERE when the "Customize" button was
            removed. It used to BE that button ("Added to order" with a tick),
            and dropping the button without replacing the indicator would have
            left a customer no way to see what is already in their order.
            On the artwork, so it reads at a glance down a column of tiles.
            The accessible name carries it too -- see the button below. */}
        {isAdded && (
          <span
            aria-hidden="true"
            className={'absolute left-2 top-2 inline-flex items-center gap-1 rounded-gift-pill '
              + 'bg-gift-accent px-2.5 py-1 text-[12px] font-bold leading-none text-white'}
          >
            <CheckIcon />
            Added
          </span>
        )}
      </div>

      {/* `flex-1` takes up the slack so every tile in a row ends level, however
          many lines its name takes.

          ⚠️ TUNED TO INK, NOT TO BOX GAPS. The caption sits 16px under the
          artwork and the price 8px under the name, measured glyph-to-glyph in
          a browser. Those are nothing like the class values: line boxes supply
          most of the distance, so a 10px margin measured 23px of visible gap.
          If you retune this, measure the rendered text -- reading the classes
          will mislead you. */}
      <div className="mt-1 flex-1">
        {/* NAME OVER PRICE, BOTH LEFT-ALIGNED, AT EVERY WIDTH -- asked for
            2026-09-28, and the third shape this row has taken in a day: first
            name-left / price-right on one row, then stacked below `md` once we
            saw it on a phone, now stacked everywhere.

            The phone measurement is what killed the one-row version: a
            two-column grid leaves each tile about 170px, and a name sharing
            that row with a three-part discounted price ("$31.96 $39.95") wrapped
            to three lines. "(8) 3x5 gift pictures" became three lines with the
            price beside the first.

            `min-w-0`/`shrink-0` and `items-baseline` all went with the row --
            they only ever existed to stop a name shoving the price off a shared
            line, and there is no shared line now. */}
        <div className="flex flex-col items-start">
          {/* REGULAR WEIGHT AND A STEP DOWN IN SIZE, both asked for
              2026-09-28 (was 700, and 15/17px). The artwork is what separates
              one product from the next on this shelf; the name only has to be
              readable under it. */}
          {/* ⚠️ THE WHOLE TILE IS THE TARGET, and this button is how.
              `after:absolute after:inset-0` stretches an invisible overlay
              across the `relative` card above, so a press anywhere on the
              artwork or the type opens the product -- which is how every other
              storefront behaves, and how Item.jsx's other render already did.

              WHY THE BUTTON IS INSIDE THE <h3> RATHER THAN AROUND THE CARD: a
              <button>'s content model is phrasing content, so wrapping a
              heading in one is invalid HTML. This way the markup stays valid,
              the heading keeps its semantics, and the accessible name is the
              PRODUCT NAME -- where before there were nine buttons on the shelf
              all called "Customize", which told a screen-reader user nothing
              about which product they were on.

              A `<button>`, not a link: this opens a configurator in component
              state, and product screens have no URL of their own. */}
          {/* `leading-[1.15]`. What read as padding between these two lines was
              never the gap -- that measured 2px -- it was the NAME'S LINE BOX,
              20.6px of it around a 14px glyph. Tightening the leading closes the
              space without moving either element.

              Not `leading-none`: names wrap to two lines on the senior shelf at
              phone width, and at 1.0 those lines touch. */}
          <h3 className="m-0 text-[14px] font-normal leading-[1.15] text-gift-ink md:text-[15px]">
            <button
              type="button"
              onClick={isAdded ? onOpenAdded : onCustomize}
              className={cn(
                'border-0 bg-transparent p-0 text-left font-normal text-inherit',
                'after:absolute after:inset-0 after:content-[""]',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-gift-accent',
              )}
            >
              {name}
              {/* The state in the accessible name, because the badge above is
                  aria-hidden and a tick is not a word. */}
              {isAdded && <span className="sr-only"> — added to order</span>}
            </button>
          </h3>

          {/* ⚠️ `priceNode` EXISTS BECAUSE THE PRICE IS NOT ALWAYS A NUMBER.
              A live offer can strike the price through and print "Free" (one
              announcement bundle under GRAD27), or show a discounted price
              beside the old one. That logic is ItemSubtitle's, and it matches
              what checkout charges -- this tile renders whatever it is handed
              rather than formatting a number that would contradict the till.

              LEFT-ALIGNED, like the name above it. The "20% off" sub-line the
              alignment used to have to accommodate is gone from this shelf
              anyway -- the banner above the grid carries it (see
              ItemSubtitle's `showOfferNote`).

              ⚠️ THE WEIGHT LIVES HERE, not inside ItemSubtitle, which renders a
              bare <div> with no weight of its own. Set it here and every case
              ItemSubtitle draws inherits it -- the plain price, and the
              discounted pair, whose own `font-bold` had to be removed for this
              to reach it. REGULAR, asked for 2026-09-28 (it was 700). The
              product screen is unaffected: ItemBuilder's wrappers set
              font-extrabold and font-semibold, and those still win there. */}
          {/* GREY, not the near-black the name uses -- the price is secondary
              to the product it belongs to. `gift-inkSoft` (#4A5260) is the
              page's existing muted text token, so this adds no new colour.
              ⚠️ It only reaches the discounted case because ItemSubtitle's
              hardcoded `text-gray-900` was removed -- see its comment.

              ⚠️ `-mt-1` CLOSES THE LAST 4px, and it is a negative margin
              rather than tighter leading on purpose: leading tight enough to
              save 4px makes a two-line name collide with itself. Measured on
              the senior shelf at 390px, where five names wrap -- the last line
              still clears the price. */}
          <div className="-mt-1 text-left text-[13px] font-normal text-gift-inkSoft md:text-[14px]">
            {priceNode || currency(price).format()}
          </div>
        </div>
      </div>
    </div>
  );
}

GiftTile.defaultProps = {
  price: 0,
  priceNode: null,
  preview: null,
  imageUrl: null,
  isAdded: false,
  onCustomize: () => {},
  onOpenAdded: () => {},
};

GiftTile.propTypes = {
  name: PropTypes.string.isRequired,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Rendered in place of the formatted `price` when given — see the note above.
  priceNode: PropTypes.node,
  // The composited preview (the customer's portrait in the product). When there
  // is none, `imageUrl` is drawn instead, and failing that a labelled
  // placeholder.
  preview: PropTypes.node,
  imageUrl: PropTypes.string,
  // From the real cart, not from component state.
  isAdded: PropTypes.bool,
  onCustomize: PropTypes.func,
  onOpenAdded: PropTypes.func,
};

export { PhotoPlaceholder };
